import { useEffect, useState } from "react";
import { keccak256, toBytes, maxUint256 } from "viem";
import { config } from "../config";
import { scoringRegistryAbi, subnetRegistryAbi } from "../abi";
import { l2Public } from "../lib/chain";
import { writeL2 } from "../lib/tx";
import { bps, fmtToken, shortAddr } from "../lib/format";
import { useWallet } from "../wallet";
import { ActionButton, Badge, Card, Field, Notice, Stat } from "../components/ui";
import { ConnectPrompt } from "./Account";

interface Subnet {
  owner: `0x${string}`;
  modelURI: string;
  registrationFee: bigint;
  ownerSplitBps: number;
  minerShareBps: number;
  active: boolean;
}

interface EpochInfo {
  epoch: bigint;
  merkleRoot: `0x${string}`;
  finalizedAt: bigint;
  signerCount: number;
  signerStake: bigint;
  invalidated: boolean;
}

export function Operate() {
  const { account } = useWallet();
  const [brainId, setBrainId] = useState(7);
  const [subnet, setSubnet] = useState<Subnet | null>(null);
  const [counts, setCounts] = useState<{ miners: bigint; validators: bigint } | null>(null);
  const [lastEpoch, setLastEpoch] = useState<EpochInfo | null>(null);
  const [modelURI, setModelURI] = useState("");
  const [ownerBps, setOwnerBps] = useState("800");
  const [minerBps, setMinerBps] = useState("7609");
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const c = config.l2.contracts;
    (async () => {
      try {
        const sn = (await l2Public.readContract({ address: c.subnetRegistry, abi: subnetRegistryAbi, functionName: "getSubnet", args: [BigInt(brainId)] })) as unknown as Subnet;
        setSubnet(sn);
        setModelURI(sn.modelURI);
        const miners = (await l2Public.readContract({ address: c.subnetRegistry, abi: subnetRegistryAbi, functionName: "minerCount", args: [BigInt(brainId)] })) as bigint;
        const validators = (await l2Public.readContract({ address: c.subnetRegistry, abi: subnetRegistryAbi, functionName: "validatorCount", args: [BigInt(brainId)] })) as bigint;
        setCounts({ miners, validators });
      } catch {
        setSubnet(null);
        setCounts(null);
      }
      try {
        setLastEpoch(null);
        const current = (await l2Public.readContract({ address: c.scoringRegistry, abi: scoringRegistryAbi, functionName: "currentEpoch" })) as bigint;
        if (current > 0n) {
          const prev = current - 1n;
          const e = (await l2Public.readContract({ address: c.scoringRegistry, abi: scoringRegistryAbi, functionName: "getEpoch", args: [prev, BigInt(brainId)] })) as unknown as Omit<EpochInfo, "epoch">;
          setLastEpoch({ ...e, epoch: prev, signerCount: Number(e.signerCount) });
        }
      } catch {
        setLastEpoch(null);
      }
    })();
  }, [brainId, nonce]);

  if (!account) return <ConnectPrompt />;

  const registry = config.l2.contracts.subnetRegistry;
  const refresh = () => setNonce((n) => n + 1);
  const isOwner = subnet && account.toLowerCase() === subnet.owner.toLowerCase();

  return (
    <>
      <div className="section-title">Operate a Brain</div>
      <Field label="Brain ID" hint="You must hold the (bridged) Brain NFT on L2 to operate it.">
        <input className="input" type="number" min={0} max={63} value={brainId} onChange={(e) => setBrainId(Math.max(0, Math.min(63, Number(e.target.value))))} style={{ maxWidth: 160 }} />
      </Field>

      <div className="grid cols-2">
        <Card title={`Brain #${brainId}`} right={subnet?.active ? <Badge kind="ok">Active</Badge> : <Badge kind="warn">Inactive</Badge>}>
          <div className="stack">
            <Stat label="Owner" value={<span className="mono" style={{ fontSize: 13 }}>{subnet ? shortAddr(subnet.owner) : "—"}</span>} />
            <div className="grid cols-2">
              <Stat label="Miners" value={counts ? counts.miners.toString() : "—"} />
              <Stat label="Validators" value={counts ? counts.validators.toString() : "—"} />
            </div>
            <div className="grid cols-2">
              <Stat label="Owner split" value={subnet ? bps(subnet.ownerSplitBps) : "—"} />
              <Stat label="Miner share (of node)" value={subnet ? bps(subnet.minerShareBps) : "—"} />
            </div>
            <Stat label="Registration fee" value={subnet ? fmtToken(subnet.registrationFee) : "—"} unit="BASED" />
            {counts && counts.validators === 0n && subnet?.active && (
              <Notice kind="warn">
                No validators registered — the validator fee share folds to the miner until one joins. Register a
                validator to enable delegated yield.
              </Notice>
            )}
            <div className="stack" style={{ gap: 6 }}>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <span className="muted">Last scoring epoch</span>
                {lastEpoch === null ? (
                  <span className="muted">—</span>
                ) : lastEpoch.merkleRoot === `0x${"0".repeat(64)}` ? (
                  <Badge kind="warn">No commitment</Badge>
                ) : lastEpoch.invalidated ? (
                  <Badge kind="warn">Invalidated</Badge>
                ) : lastEpoch.finalizedAt > 0n ? (
                  <Badge kind="ok">Finalized</Badge>
                ) : (
                  <Badge>Challenge window</Badge>
                )}
              </div>
              {lastEpoch !== null && lastEpoch.merkleRoot !== `0x${"0".repeat(64)}` && (
                <div className="hint">
                  epoch {lastEpoch.epoch.toString()} · {lastEpoch.signerCount} signer{lastEpoch.signerCount === 1 ? "" : "s"} ·{" "}
                  {fmtToken(lastEpoch.signerStake)} BASED signing stake
                </div>
              )}
            </div>
          </div>
        </Card>

        <div className="stack">
          <Card title="Brain configuration" sub={isOwner ? "You own this Brain." : "Owner-only actions."}>
            <Field label="Model URI" hint="e.g. a Hugging Face URL. The model hash is derived as keccak256(modelURI).">
              <input className="input" value={modelURI} onChange={(e) => setModelURI(e.target.value)} placeholder="ipfs://… or https://huggingface.co/…" />
            </Field>
            <div className="row">
              {!subnet?.active ? (
                <ActionButton
                  disabled={!isOwner && subnet !== null}
                  onRun={() => writeL2(account, { address: registry, abi: subnetRegistryAbi, functionName: "activate", args: [BigInt(brainId), keccak256(toBytes(modelURI)), modelURI] })}
                  onDone={refresh}
                >
                  Activate Brain
                </ActionButton>
              ) : (
                <ActionButton
                  disabled={!isOwner}
                  onRun={() => writeL2(account, { address: registry, abi: subnetRegistryAbi, functionName: "setModel", args: [BigInt(brainId), keccak256(toBytes(modelURI)), modelURI] })}
                  onDone={refresh}
                >
                  Update model
                </ActionButton>
              )}
            </div>
          </Card>

          <Card title="Fee split" sub="Owner cut of gross; miner share of the remaining node split.">
            <div className="grid cols-2">
              <Field label="Owner split (bps)" hint="Max 1500 (15%).">
                <input className="input" value={ownerBps} onChange={(e) => setOwnerBps(e.target.value)} />
              </Field>
              <Field label="Miner share (bps)" hint="Of the node share; rest goes to validators.">
                <input className="input" value={minerBps} onChange={(e) => setMinerBps(e.target.value)} />
              </Field>
            </div>
            <ActionButton
              disabled={!isOwner}
              onRun={() => writeL2(account, { address: registry, abi: subnetRegistryAbi, functionName: "setEmissionSplit", args: [BigInt(brainId), Number(ownerBps), Number(minerBps)] })}
              onDone={refresh}
            >
              Update split
            </ActionButton>
          </Card>
        </div>
      </div>

      <div className="section-title">Join this Brain</div>
      <div className="grid cols-2">
        <Card title="Register as miner" sub="Serve inference on this Brain's model.">
          <p className="muted" style={{ fontSize: 14 }}>
            Registration burns the Brain&apos;s fee ({subnet ? fmtToken(subnet.registrationFee) : "—"} BASED). Run the
            reference miner (<span className="mono">basedai-miner</span>) pointed at this Brain.
          </p>
          <div className="row">
            <ActionButton onRun={() => writeL2(account, { address: registry, abi: subnetRegistryAbi, functionName: "registerMiner", args: [BigInt(brainId), maxUint256] })} onDone={refresh}>
              Register miner
            </ActionButton>
            <ActionButton variant="ghost" onRun={() => writeL2(account, { address: registry, abi: subnetRegistryAbi, functionName: "deregisterMiner", args: [BigInt(brainId)] })} onDone={refresh}>
              Deregister
            </ActionButton>
          </div>
        </Card>

        <Card title="Register as validator" sub="Score miners and earn the validator fee share.">
          <p className="muted" style={{ fontSize: 14 }}>
            Validators need delegated stake to carry quorum weight — see the Stake page. Run{" "}
            <span className="mono">basedai-validator</span>.
          </p>
          <div className="row">
            <ActionButton onRun={() => writeL2(account, { address: registry, abi: subnetRegistryAbi, functionName: "registerValidator", args: [BigInt(brainId), maxUint256] })} onDone={refresh}>
              Register validator
            </ActionButton>
            <ActionButton variant="ghost" onRun={() => writeL2(account, { address: registry, abi: subnetRegistryAbi, functionName: "deregisterValidator", args: [BigInt(brainId)] })} onDone={refresh}>
              Deregister
            </ActionButton>
          </div>
        </Card>
      </div>
    </>
  );
}
